import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormatPolicyNumberService } from 'app/commercial/claims/services/format-policy-number.service';
import { environment } from 'environments/environment';
import { Subject, Subscription } from 'rxjs';
import { LoggerService } from '../../../shared/services/logger.service';
import { SessionService } from '../../../shared/services/session.service';
import { SearchForClaimsService } from '../services/search-for-claims.service';
import { CommercialLoggingService } from './commercial-logging.service';

@Injectable()
export class ClaimsDataService {
    static readonly claimsConstants = {
        policyInfo: [
            {
                prefixes: ['BA'],
                fnolFlow: 'Commercial Auto'
            },
            {
                prefixes: ['BP', 'CI', 'CO', 'CP', 'CR', 'FM', 'GP', 'LM', 'FIMV'],
                fnolFlow: 'Commercial Property'
            },
            {
                prefixes: ['CA', 'CG', 'FL', 'GL', 'FAEV', 'FAEN'],
                fnolFlow: 'General Liability'
            },
            {
                prefixes: ['WC'],
                fnolFlow: 'Workers Compensation'
            }
        ]
    };

    ecn: string;
    guid: string;
    lossCauseTypes: string[] = [];
    lossCauseTypesLoaded = false;
    searchClaimSubscription = new Subscription();
    userId: string;
    selectedAccountNumber: any;
    private claimDetails: any;
    private claimEvents: any;
    private claimNavigationData: any;
    private claimNumber = '';
    private claimsCount = '';
    private claimsPolicyTypes: string[] = [
        'General Liability',
        'Workers Compensation',
        'Commercial Auto',
        'Commercial Property'
    ];
    private fnolFlow = '';
    private fromSearch = false;
    private isClaimsFlow = false;
    private isClaimsFlowSubject = new Subject<any>();
    isClaimsFlowEmitter = this.isClaimsFlowSubject.asObservable(); // eslint-disable-line @typescript-eslint/member-ordering
    private package: any;
    private pakNumber = '';
    private policyType = '';
    private premisisID: any;
    private searching = false;
    private selectedPolicyNumber = '';

    constructor(
        private httpClient: HttpClient,
        private LOGGER: LoggerService,
        private commercialLoggingService: CommercialLoggingService,
        private searchClaimsService: SearchForClaimsService,
        private formatPolicyNumberService: FormatPolicyNumberService,
        private sessionService: SessionService
    ) {
        if (!this.getClaimsNavigationData()) {
            this.initializeClaimNavigationData();
        }
        this.setJSON();
    }
    clearClaimsNavigationData(): void {
        sessionStorage.setItem('claimNavigationData', '{}');
        this.claimNavigationData = {};
    }

    clearCommercialClaimsSession(): void {
        this.clearClaimsNavigationData();
        sessionStorage.removeItem('claimsSelectedAccountNumber');
        sessionStorage.removeItem('claimsSelectedPolicyNumber');
        sessionStorage.removeItem('claimsPolicyType');
        sessionStorage.removeItem('claimsPakNumber');
        sessionStorage.removeItem('fnolFlow');
        sessionStorage.removeItem('Claim-Number-Stored-In-Session');

        this.selectedAccountNumber = undefined;
        this.selectedPolicyNumber = '';
        this.policyType = '';
        this.pakNumber = '';
        this.fnolFlow = '';
        this.claimDetails = undefined;
        this.claimEvents = undefined;
        this.claimNumber = '';
        this.claimsCount = '';
        this.package = undefined;
        this.premisisID = undefined;
    }
    determineFnolFlow(policy: any): string {
        let prefix = policy.prefixB;
        if (prefix === undefined) {
            prefix = policy.policyPrefix;
        }
        if (prefix === undefined) {
            this.commercialLoggingService.logServiceFailure(
                this.LOGGER,
                'Commercial Claims Data',
                'ClaimsDataService',
                'determineFnolFlow',
                this.getServiceLogMetadata(),
                { code: 'PREFIX_NOT_FOUND' }
            );
            return '';
        } else {
            for (const type of ClaimsDataService.claimsConstants.policyInfo) {
                for (const key of type.prefixes) {
                    if (prefix.startsWith(key)) {
                        return type.fnolFlow;
                    }
                }
            }
            this.commercialLoggingService.logServiceFailure(
                this.LOGGER,
                'Commercial Claims Data',
                'ClaimsDataService',
                'determineFnolFlow',
                this.getServiceLogMetadata(),
                { code: 'PREFIX_NOT_MATCHED' }
            );
            return '';
        }
    }
    formatLossLocationDisplay(lossLocation: any): string {
        let displayAddress = '';
        if (lossLocation.city) {
            // eslint-disable-next-line no-useless-concat
            displayAddress = `${lossLocation.city},` + ` `;
        }
        if (lossLocation.state) {
            displayAddress = displayAddress + lossLocation.state;
        }
        if (lossLocation.postalCode) {
            displayAddress = `${displayAddress} ${lossLocation.postalCode}`;
        }
        return displayAddress;
    }
    getClaimEvents(): any {
        if (!this.claimEvents) {
            this.claimEvents = this.getClaimsNavigationData().claimEvents;
        }
        return this.claimEvents;
    }

    getClaimInformation(): any {
        if (!this.claimDetails) {
            this.claimDetails = this.getClaimsNavigationData().claimDetails;
        }
        return this.claimDetails;
    }

    getClaimNumber(): string {
        if (!this.claimNumber) {
            this.claimNumber = this.getClaimsNavigationData().claimNumber;
        }
        return this.claimNumber;
    }

    getClaimsAccountNumber(): any {
        if (!this.selectedAccountNumber) {
            this.selectedAccountNumber = this.getClaimsNavigationData().claimsSelectedAccountNumber;
        }
        if (!this.selectedAccountNumber) {
            this.selectedAccountNumber = sessionStorage.getItem('claimsSelectedAccountNumber');
        }
        return this.selectedAccountNumber;
    }
    getClaimsDisplayCount(): any {
        if (!this.claimsCount) {
            this.claimsCount = this.getClaimsNavigationData().claimsCount;
        }
        return this.claimsCount;
    }

    getClaimsPackage(): any {
        if (!this.package) {
            this.package = this.getClaimsNavigationData().package;
        }
        return this.package;
    }
    getClaimsPakNumber(): string {
        if (!this.pakNumber) {
            this.pakNumber = this.getClaimsNavigationData().pakNumber;
        }
        if (!this.pakNumber) {
            this.pakNumber = sessionStorage.getItem('claimsPakNumber') || '';
        }
        return this.pakNumber;
    }
    getClaimsPolicyNumber(): string {
        if (!this.selectedPolicyNumber) {
            this.selectedPolicyNumber = this.getClaimsNavigationData().claimsSelectedPolicyNumber;
        }
        if (!this.selectedPolicyNumber) {
            this.selectedPolicyNumber = sessionStorage.getItem('claimsSelectedPolicyNumber') || '';
        }
        if (!this.selectedPolicyNumber) {
            this.selectedPolicyNumber = this.sessionService.getPolicyNumber();
        }
        return this.selectedPolicyNumber;
    }

    getClaimsLookupIdentifier(): string {
        return this.getClaimsAccountNumber() || this.getClaimsPolicyNumber();
    }

    getClaimsPolicyType(): string {
        if (!this.policyType) {
            this.policyType = this.getClaimsNavigationData().claimsPolicyType;
        }
        if (!this.policyType) {
            this.policyType = sessionStorage.getItem('claimsPolicyType') || '';
        }
        return this.policyType;
    }
    getClaimsPolicyTypeEnums(): string[] {
        return this.claimsPolicyTypes;
    }
    getFnolFlow(): string {
        if (!this.fnolFlow) {
            this.fnolFlow = this.getClaimsNavigationData().fnolFlow;
        }
        if (!this.fnolFlow) {
            this.fnolFlow = sessionStorage.getItem('fnolFlow') || '';
        }
        return this.fnolFlow;
    }
    getIsClaimsFlow(): any {
        if (!this.isClaimsFlow) {
            return this.getClaimsNavigationData().isClaimsFlow;
        }
        return this.isClaimsFlow;
    }
    getJSON(): any {
    return this.httpClient.get(`${environment.absoluteDomain}/assets/lossCauseConstants.json`);
    }
    getLossCause(lossCause: string): string | undefined {
        if (!this.lossCauseTypes || this.lossCauseTypes.length === 0) {
            return undefined;
        }

         for (const lossCauseType of this.lossCauseTypes as unknown as Array<Record<string, string>>) {
            const element = lossCauseType;
            if (Object.keys(element).includes(lossCause)) {
                const display = element[lossCause];
                return display;
            }
        }
        return undefined;
    }
    getPremisisID(): any {
        if (!this.premisisID) {
            this.premisisID = this.getClaimsNavigationData().premisisID;
        }
        return this.premisisID;
    }

    getPrimary(objectArray: any): any {
        if (objectArray) {
            for (const index in objectArray) {
                if (objectArray[index].type === 'P') {
                    return objectArray[index];
                }
            }
        } else {
            return undefined;
        }
        return objectArray[0];
    }
    isFromSearch(): boolean {
        if (!this.fromSearch) {
            this.fromSearch = this.getClaimsNavigationData().fromSearch;
        }
        return this.fromSearch;
    }
    isSearching(): boolean {
        return this.searching;
    }
    retrievePremisisID(claimInfo: any): void {
        this.searchClaimSubscription.unsubscribe();
        this.searchClaimSubscription = new Subscription();

        const pak = this.getClaimsPackage();
        const prefixA = this.formatPolicyNumberService.getFormattedPrefix(pak.policyAccount.prefixA);
        const branchNumber = pak.policyAccount.branchNumber;
        const decNumber = pak.policyAccount.decNumber;
        const policyNumber = pak.policyAccount.policyNumber;
        let pendingRequests = pak.policies.length;
        let hasMatch = false;
        let hasError = false;

        if (pendingRequests === 0) {
            this.searching = false;
            this.premisisID = 'Not found';
            return;
        }

        const completeLookup = (): void => {
            pendingRequests--;
            if (pendingRequests > 0 || hasMatch) {
                return;
            }

            this.searching = false;
            this.premisisID = hasError ? 'Error retrieving premisis ID' : 'Not found';
        };

        this.searching = true;
        this.premisisID = 'Searching';
        pak.policies.forEach((policy: any) => {
            if (hasMatch) {
                return;
            }

            const prefixB = this.formatPolicyNumberService.getFormattedPrefix(policy.prefixB);
            const fullPolicyNumber = prefixA + prefixB + branchNumber + decNumber + policyNumber;
            const requestBody = { policyNumber: fullPolicyNumber };
            const queryParameters = `?incidentOnly=true&limit=30&offset=${0}`;

            const searchSubscription = this.searchClaimsService.searchClaims(requestBody, queryParameters).subscribe({
                next: (response) => {
                    const claimList = response.body.claimSummaries;

                    if (!hasMatch) {
                        const matchedClaim = claimList.find((claim: any) => claim.claimNumber === claimInfo.claimNumber && claim.premisisID?.length > 0);

                        if (matchedClaim) {
                            hasMatch = true;
                            this.searching = false;
                            this.commercialLoggingService.logServiceSuccess(
                                this.LOGGER,
                                'Commercial Claims Data',
                                'ClaimsDataService',
                                'retrievePremisisID',
                                this.getServiceLogMetadata({ claimNumber: claimInfo.claimNumber, claimsDisplayCount: matchedClaim.premisisID.length })
                            );
                            this.premisisID = '';
                            matchedClaim.premisisID.forEach((id: any, premisisIndex: number) => {
                                this.premisisID = this.premisisID + (premisisIndex > 0 ? ', ' : '') + id;
                            });
                            this.searchClaimSubscription.unsubscribe();
                            this.searchClaimSubscription = new Subscription();
                        }
                    }

                    completeLookup();
                },
                error: (error) => {
                    hasError = true;
                    this.commercialLoggingService.logServiceFailure(
                        this.LOGGER,
                        'Commercial Claims Data',
                        'ClaimsDataService',
                        'retrievePremisisID',
                        this.getServiceLogMetadata({ claimNumber: claimInfo?.claimNumber || '' }),
                        error
                    );
                    completeLookup();
                }
            });

            this.searchClaimSubscription.add(searchSubscription);
        });
    }
    setClaimEvents(claimEvents: any): void {
        this.setClaimsNavigationData('claimEvents', claimEvents);
        this.claimEvents = this.getClaimsNavigationData().claimEvents;
    }
    setClaimInformation(claimDetails: any): any {
        this.setClaimsNavigationData('claimDetails', claimDetails);
        this.claimDetails = this.getClaimsNavigationData().claimDetails;
    }
    setClaimNumber(claimNumber: string): any {
        this.setClaimsNavigationData('claimNumber', claimNumber);
        this.claimNumber = this.getClaimsNavigationData().claimNumber;
    }
    setClaimsAccountNumber(packages: any): any {
        const numberOfSpaces = 5;
        const accountNumber = `${packages.policyAccount.prefixA + ' '.repeat(numberOfSpaces) +
            packages.policyAccount.branchNumber}0${packages.policyAccount.policyNumber}`;
        this.setClaimsNavigationData('claimsSelectedAccountNumber', accountNumber);
        sessionStorage.setItem('claimsSelectedAccountNumber', accountNumber);
        this.selectedAccountNumber = this.getClaimsNavigationData().claimsSelectedAccountNumber;
    }
    setClaimsDisplayCount(claimsCount: any): any {
        this.setClaimsNavigationData('claimsCount', claimsCount);
        this.claimsCount = this.getClaimsNavigationData().claimsCount;
    }

    setClaimsPackage(packageData: any): any {
        this.setClaimsNavigationData('package', packageData);
        this.package = this.getClaimsNavigationData().package;
    }

    hydratePackageContext(packageData: any): void {
        if (!packageData?.policyAccount) {
            return;
        }

        this.setClaimsPackage(packageData);
        this.setClaimsAccountNumber(packageData);
        this.setClaimsPakNumber(this.buildPakNumber(packageData));
    }
    setClaimsPakNumber(pakNumber: string): any {
        this.setClaimsNavigationData('claimsPakNumber', pakNumber);
        sessionStorage.setItem('claimsPakNumber', pakNumber);
        this.pakNumber = this.getClaimsNavigationData().claimsPakNumber;
    }
    setClaimsPolicyNumber(policyNumber: string): void {
        this.setClaimsNavigationData('claimsSelectedPolicyNumber', policyNumber);
        sessionStorage.setItem('claimsSelectedPolicyNumber', policyNumber);
        this.selectedPolicyNumber = this.getClaimsNavigationData().claimsSelectedPolicyNumber;
    }
    setClaimsPolicyType(policyType: string): any {
        this.setClaimsNavigationData('claimsPolicyType', policyType);
        sessionStorage.setItem('claimsPolicyType', policyType);
        this.policyType = this.getClaimsNavigationData().claimsPolicyType;
    }
    setFnolFlow(policy: any): void {
        const flow = this.determineFnolFlow(policy);
        this.setClaimsNavigationData('fnolFlow', flow);
        sessionStorage.setItem('fnolFlow', flow);
        this.fnolFlow = this.getClaimsNavigationData().fnolFlow;
    }
    setFromSearch(fromSearch: boolean): void {
        this.setClaimsNavigationData('fromSearch', fromSearch);
        this.fromSearch = fromSearch;
    }
    setIsClaimsFlow(flag: boolean): void {
        const data = JSON.parse(sessionStorage.getItem('claimNavigationData') || '{}');
        if (data != null) {
            data.isClaimsFlow = flag;
        }
        sessionStorage.setItem('claimNavigationData', JSON.stringify(data));
        this.claimNavigationData = JSON.parse(sessionStorage.getItem('claimNavigationData') || '{}');
        this.isClaimsFlow = this.getClaimsNavigationData().isClaimsFlow;
        this.isClaimsFlowSubject.next(flag);
    }
    setIsSearching(isSearching: boolean): void {
        this.searching = isSearching;
    }
    setJSON(): any {
        this.getJSON().subscribe((data: any) => {
            this.lossCauseTypes = data;
            this.lossCauseTypesLoaded = true;
        }, (error: any) => {
            this.commercialLoggingService.logServiceFailure(
                this.LOGGER,
                'Commercial Claims Data',
                'ClaimsDataService',
                'setJSON',
                this.getServiceLogMetadata(),
                error
            );
        });
    }
    setPremisisID(premisisID: any): any {
        this.setClaimsNavigationData('premisisID', premisisID);
        this.premisisID = this.getClaimsNavigationData().premisisID;
    }
    private getClaimsNavigationData(): any {
        if (!this.claimNavigationData) {
            if (!sessionStorage.getItem('claimNavigationData')) {
                this.initializeClaimNavigationData();
            }
            this.claimNavigationData = JSON.parse(sessionStorage.getItem('claimNavigationData') || '{}');
            if (!this.claimNavigationData) {
                this.initializeClaimNavigationData();
                this.claimNavigationData = {};
            }
        }
        return this.claimNavigationData;
    }

    private initializeClaimNavigationData(): void {
        sessionStorage.setItem('claimNavigationData', '{}');
    }

    private setClaimsNavigationData(key: string, value: any): any {
        const claimNavigationData = this.getClaimsNavigationData();
        claimNavigationData[key] = value;
        sessionStorage.setItem('claimNavigationData', JSON.stringify(claimNavigationData));
        this.claimNavigationData = claimNavigationData;
    }

    private buildPakNumber(packageData: any): string {
        const policyAccount = packageData?.policyAccount;
        if (!policyAccount) {
            return '';
        }

        return `${policyAccount.prefixA} ${policyAccount.branchNumber}${policyAccount.decNumber}${policyAccount.policyNumber}`;
    }

    private getServiceLogMetadata(additionalFields: any = {}): any {
        return {
            policyNumber: this.getClaimsPolicyNumber(),
            policyType: this.getClaimsPolicyType() || 'UNKNOWN',
            lineOfBusiness: this.getFnolFlow() || 'UNKNOWN',
            claimsDisplayCount: this.getClaimsDisplayCount() || 'UNKNOWN',
            alphaState: this.getPrimary(this.getClaimsPackage()?.address)?.state || 'UNKNOWN',
            previousUrl: window.sessionStorage.getItem('previousUrl') || '',
            ...additionalFields
        };
    }
}
